/**
 * HistoryItem Component
 * Displays a single history item
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Format file size for display
 * @param {number} bytes
 * @returns {string}
 */
function formatFileSize(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

/**
 * Format date for display
 * @param {string} dateString
 * @returns {string}
 */
function formatDate(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  
  return date.toLocaleDateString();
}

/**
 * Get icon based on file type
 * @param {string} fileType
 * @returns {JSX.Element}
 */
function FileIcon({ fileType }) {
  const isVideo = fileType?.startsWith('video/');
  
  if (isVideo) {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
        <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
      </svg>
    );
  }
  
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.972 7.972 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.983 5.983 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.414z" clipRule="evenodd" />
    </svg>
  );
}

/**
 * History item component
 * @param {Object} props
 * @param {Object} props.item - History item data
 * @param {Function} props.onLoad - Load handler
 * @param {Function} props.onDelete - Delete handler
 * @param {Function} props.onToggleFavorite - Favorite toggle handler
 */
export function HistoryItem({ item, onLoad, onDelete, onToggleFavorite }) {
  const { t } = useTranslation(['history', 'common']);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!showConfirm) {
      setShowConfirm(true);
      return;
    }
    
    setIsDeleting(true);
    try {
      await onDelete(item.id);
    } finally {
      setIsDeleting(false);
      setShowConfirm(false);
    }
  };

  const handleLoad = () => {
    onLoad(item);
  };

  const handleToggleFavorite = (e) => {
    e.stopPropagation();
    onToggleFavorite(item.id);
  };

  return (
    <div className="group bg-white rounded-lg border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all duration-200">
      <div 
        className="p-4 cursor-pointer"
        onClick={handleLoad}
      >
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center text-primary-600 flex-shrink-0">
            <FileIcon fileType={item.fileType} />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h4 className="font-medium text-gray-900 truncate">
                {item.fileName}
              </h4>
              
              {/* Favorite button */}
              <button
                onClick={handleToggleFavorite}
                className={`flex-shrink-0 p-1 rounded transition-colors ${
                  item.favorite 
                    ? 'text-yellow-500 hover:text-yellow-600' 
                    : 'text-gray-300 hover:text-yellow-400'
                }`}
                title={item.favorite ? 'Remove from favorites' : 'Add to favorites'}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              </button>
            </div>

            {/* Summary preview */}
            {item.result?.summary && (
              <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                {item.result.summary}
              </p>
            )}

            {/* Meta info */}
            <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
              <span>{formatDate(item.date)}</span>
              <span>•</span>
              <span>{formatFileSize(item.fileSize)}</span>
              {item.edited && (
                <>
                  <span>•</span>
                  <span className="text-primary-600">Edited</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete confirmation */}
      {showConfirm && (
        <div className="px-4 pb-4 pt-0">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-red-600">{t('item.deleteConfirm')}</span>
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 disabled:opacity-50"
            >
              {isDeleting ? t('common:actions.deleting') : t('common:actions.yes')}
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setShowConfirm(false); }}
              className="px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
            >
              {t('common:actions.no')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
